import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import { parseLocalDate } from '@/utils/dateUtils';
import filters from '@/filters';
import { useBenefitUsageGroups } from '@/composables/useBenefitUsageGrouping';
import { useIsMobile } from '@/composables/useIsMobile';
import { notify } from 'notiwind';
import dayjs from 'dayjs';

export function useBenefitDetails() {
    const store = useCardBenefitsStore();
    const {
        selectedBenefit,
        sidebarOpen,
        showLastFour,
        currentBenefitPosition,
        totalBenefitsCount,
        isFirstBenefit,
        isLastBenefit,
    } = storeToRefs(store);

    const {
        closeBenefitDetails,
        navigateToPreviousBenefit,
        navigateToNextBenefit,
        setShowAddUsageModal,
        calculateDisplayAmount,
    } = store;

    const { isMobile } = useIsMobile();

    // Modal state
    const showEditUsageModal = ref(false);
    const showDeleteModal = ref(false);
    const showRenewalDateModal = ref(false);
    const entryToDelete = ref(null);
    // MY1-3081: the optional "What's wrong?" answer when deleting an
    // auto-tracked record.
    const deleteFeedback = ref('');
    const editingEntry = ref(null);
    const formErrors = ref({});
    const usageFormData = ref({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        notes: '',
    });

    const visibilitySaving = ref(false);

    const isBenefitHidden = computed(
        () => selectedBenefit.value?.user_override?.is_hidden === true
    );

    const toggleBenefitVisibility = async () => {
        if (!selectedBenefit.value || visibilitySaving.value) return;
        visibilitySaving.value = true;
        try {
            const hiding = !isBenefitHidden.value;
            await store.setBenefitVisibility({
                cardBenefitId: selectedBenefit.value.benefit?.card_benefit_id,
                connectedAccountId: selectedBenefit.value.connected_account_id,
                isHidden: hiding,
            });
            notify({
                group: 'success',
                text: hiding
                    ? 'Benefit hidden'
                    : 'Benefit visibility set to shown',
            });
        } catch (error) {
            console.error('Failed to update benefit visibility:', error);
        } finally {
            visibilitySaving.value = false;
        }
    };

    const cdnUrl = computed(() => store.cdnUrl);

    // History groups — for compound benefits, merge sub-benefit usage records
    // into a synthetic copy of the parent so the history shows all redemptions.
    const benefitRef = computed(() => {
        const benefit = selectedBenefit.value;
        if (!benefit) return benefit;

        const subBenefits = benefit.sub_benefits;
        if (!Array.isArray(subBenefits) || subBenefits.length === 0)
            return benefit;

        const subRecords = subBenefits.flatMap((sub) => {
            const records = sub.usage_records ?? [];
            return records.map((r) => ({
                ...r,
                _sub_benefit_title: sub.benefit_title,
            }));
        });

        if (subRecords.length === 0) return benefit;

        return {
            ...benefit,
            usage_records: [...(benefit.usage_records ?? []), ...subRecords],
        };
    });
    const { groups: historyGroups, labelFor } = useBenefitUsageGroups(
        benefitRef,
        null,
        store.calculateDisplayAmount,
        store.calculateProratedAnnualValue
    );

    // Display amount
    const displayAmount = computed(() => {
        const benefit = selectedBenefit.value || {};
        if (!benefit) return 0;
        return calculateDisplayAmount(benefit);
    });

    // Version history for selected date
    const effectiveVersionForSelectedDate = computed(() => {
        const benefit = selectedBenefit.value;
        if (!benefit || !usageFormData.value.date) return null;

        const versionHistory = benefit.version_history || [];
        if (versionHistory.length === 0) return null;

        const selectedDate = dayjs(usageFormData.value.date);

        const exactMatch = versionHistory.find((version) => {
            const effectiveStart = dayjs(version.effective_start);
            const effectiveEnd = version.effective_end
                ? dayjs(version.effective_end)
                : null;
            const afterStart =
                selectedDate.isSame(effectiveStart, 'day') ||
                selectedDate.isAfter(effectiveStart);
            const beforeEnd =
                !effectiveEnd || selectedDate.isBefore(effectiveEnd);
            return afterStart && beforeEnd;
        });

        if (exactMatch) return exactMatch;

        const sortedVersions = [...versionHistory].sort(
            (a, b) =>
                dayjs(a.effective_start).valueOf() -
                dayjs(b.effective_start).valueOf()
        );
        const earliestVersion = sortedVersions[0];

        if (
            earliestVersion &&
            selectedDate.isBefore(dayjs(earliestVersion.effective_start))
        ) {
            return earliestVersion;
        }

        return null;
    });

    const hasActiveVersionForSelectedDate = computed(() => {
        return effectiveVersionForSelectedDate.value !== null;
    });

    // Display amount for selected date.
    //
    // For non-monthly benefits we always defer to `calculateDisplayAmount`
    // (the same function the sidebar's progress bar and the Current /
    // Insights row popovers use), so the sidebar's Add usage form agrees
    // with what the user sees a few pixels above it.
    //
    // Previously this function tried to be version-aware: when
    // `version_history` was non-empty and a version covered the selected
    // date, it returned `version.amount` directly. But `version.amount`
    // is stored as the per-unit amount (e.g. $100 for a tiered "two $100
    // credits" benefit whose true period value is $200), so for tiered or
    // count-based benefits this collapsed `remainingAmount` to 0 and
    // surfaced "Fully redeemed!" in the popover even when the progress bar
    // above showed e.g. $189 of $200 (MY1-2776).
    //
    // Validation (`validate()` below) still blocks the submission with a
    // "No benefit version was active on this date" error when the user
    // picks a date that no stored version covers, so we keep the safety
    // guard on save without making the display lie.
    //
    // Monthly benefits still go through the override-month / count
    // multiplier branch because that handles real customer-facing
    // behavior (e.g. December's bumped-up Uber credit).
    const displayAmountForSelectedDate = computed(() => {
        const benefit = selectedBenefit.value;
        if (!benefit) return 0;

        const effectiveVersion = effectiveVersionForSelectedDate.value;

        const baseAmount = effectiveVersion
            ? parseFloat(effectiveVersion.amount) || 0
            : parseFloat(benefit.benefit?.amount) || 0;

        const valueDetails =
            effectiveVersion?.value_details ||
            benefit.benefit?.value_details ||
            {};
        const periodType =
            effectiveVersion?.period_type || benefit.benefit?.period_type;

        if (periodType?.toLowerCase() === 'monthly') {
            const overrideMonth = valueDetails.override_month?.month;
            const overrideValue = valueDetails.override_month?.value;

            if (
                overrideMonth &&
                overrideValue != null &&
                usageFormData.value.date
            ) {
                const selectedMonth = String(
                    dayjs(usageFormData.value.date).month() + 1
                ).padStart(2, '0');
                if (overrideMonth === selectedMonth) {
                    let periodValue = parseFloat(overrideValue) || 0;
                    const count = parseFloat(valueDetails.count) || 1;
                    if (count > 1) {
                        periodValue *= count;
                    }
                    return periodValue;
                }
            }

            const count = parseFloat(valueDetails.count) || 1;
            const hasCountMultiplier =
                benefit.benefit?.values_summary?.is_count_based;
            return hasCountMultiplier ? baseAmount * count : baseAmount;
        }

        return calculateDisplayAmount(benefit);
    });

    const isOverrideMonth = computed(() => {
        const benefit = selectedBenefit.value;
        if (
            !benefit ||
            benefit.benefit?.period_type?.toLowerCase() !== 'monthly'
        ) {
            return false;
        }
        const overrideMonth =
            benefit.benefit.value_details?.override_month?.month;
        if (!overrideMonth) return false;
        const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0');
        return overrideMonth === currentMonth;
    });

    const normalMonthlyValue = computed(() => {
        const benefit = selectedBenefit.value;
        if (!benefit) return 0;
        const valueDetails = benefit.benefit?.value_details || {};
        const count = parseFloat(valueDetails.count) || 1;
        const baseAmount = parseFloat(benefit.benefit?.amount) || 0;
        const hasCountMultiplier =
            benefit.benefit?.values_summary?.is_count_based;
        return hasCountMultiplier ? baseAmount * count : baseAmount;
    });

    const usageForSelectedPeriod = computed(() => {
        if (!usageFormData.value.date || !selectedBenefit.value) return 0;

        const periodType = selectedBenefit.value.benefit?.period_type;
        const selectedPeriodKey = store.getPeriodKey(
            usageFormData.value.date,
            periodType
        );
        const currentPeriodKey = store.getPeriodKey(new Date(), periodType);

        if (selectedPeriodKey === currentPeriodKey) {
            return currentPeriodUsage.value;
        }

        const usageRecords = selectedBenefit.value.usage_records || [];
        return usageRecords
            .filter(
                (record) =>
                    store.getPeriodKey(record.used_at, periodType) ===
                    selectedPeriodKey
            )
            .reduce(
                (sum, record) =>
                    sum + (parseFloat(record.total_amount_used) || 0),
                0
            );
    });

    const isCountBased = computed(() => {
        return store.checkIsCountBased(selectedBenefit.value?.benefit);
    });

    const countLabel = computed(() => {
        return selectedBenefit.value?.benefit?.value_details?.count_label || '';
    });

    const remainingAmount = computed(() => {
        const benefitAmount = displayAmountForSelectedDate.value;
        const usedAmount = usageForSelectedPeriod.value;
        return Math.max(0, benefitAmount - usedAmount).toFixed(2);
    });

    // By-count benefits that still carry a dollar value (e.g. 2 × $250): the
    // user enters a $ amount (isCountBased is false), but the meaningful
    // "remaining" unit is the number of uses, capped at value_details.count by
    // the backend ValidateBenefitUsageCount rule.
    const isByCountValueBenefit = computed(() => {
        if (isCountBased.value) {
            return false;
        }
        const count =
            parseFloat(selectedBenefit.value?.benefit?.value_details?.count) ||
            1;
        return count > 1;
    });

    // Uses recorded in the selected date's period. For by-count benefits with a
    // dollar value each record is one use — count records (usage_count may be 0
    // on records logged before the benefit became by-count). Pure count-based
    // benefits sum usage_count, using the recalculated count for the current
    // period (handles anniversary reset basis + optimistic updates).
    const countUsedForSelectedPeriod = computed(() => {
        if (!usageFormData.value.date || !selectedBenefit.value) return 0;

        const periodType = selectedBenefit.value.benefit?.period_type;
        const selectedPeriodKey = store.getPeriodKey(
            usageFormData.value.date,
            periodType
        );
        const currentPeriodKey = store.getPeriodKey(new Date(), periodType);

        const recordsInPeriod = (
            selectedBenefit.value.usage_records || []
        ).filter(
            (record) =>
                store.getPeriodKey(record.used_at, periodType) ===
                selectedPeriodKey
        );

        if (isByCountValueBenefit.value) {
            return recordsInPeriod.length;
        }

        if (selectedPeriodKey === currentPeriodKey) {
            return (
                store.recalculateCurrentPeriodUsage(selectedBenefit.value)
                    .count_used_current_period || 0
            );
        }
        return recordsInPeriod.reduce(
            (sum, record) => sum + (parseFloat(record.usage_count) || 0),
            0
        );
    });

    const remainingCount = computed(() => {
        const isNcred =
            selectedBenefit.value?.benefit?.values_summary?.currency ===
            'NCRED';
        const maxCount =
            selectedBenefit.value?.benefit?.value_details?.count ||
            (isNcred ? displayAmount.value : 0) ||
            0;
        return Math.max(0, maxCount - countUsedForSelectedPeriod.value);
    });

    // Per-transaction cap for by-count benefits (value_details.count > 1): a
    // single entry can't exceed the version's per-use amount even when the
    // period still has room (e.g. a 2 × $250 credit caps each entry at $250
    // though $500 remains). Mirrors the backend
    // ValidateBenefitUsagePerTransactionAmount rule. Derived from the period
    // value so version selection and monthly override-month handling stay in
    // sync with remainingAmount; Infinity (no cap) for single-allotment benefits.
    const perTransactionMaxAmount = computed(() => {
        const count =
            parseFloat(selectedBenefit.value?.benefit?.value_details?.count) ||
            1;
        if (count <= 1) return Infinity;
        return (parseFloat(displayAmountForSelectedDate.value) || 0) / count;
    });

    const utilizationPercentage = computed(() => {
        if (!selectedBenefit.value) return 0;
        let maxValue;
        if (isCountBased.value) {
            maxValue =
                selectedBenefit.value?.benefit?.value_details?.count ||
                displayAmount.value ||
                0;
        } else {
            maxValue = displayAmount.value;
        }
        const usedValue = currentPeriodUsage.value;
        return maxValue > 0 ? Math.round((usedValue / maxValue) * 100) : 0;
    });

    const isBenefitFullyRedeemedThisYear = computed(() => {
        return store.isBenefitFullyRedeemedThisYear(selectedBenefit.value);
    });

    const showFullyRedeemedIcon = computed(() => {
        const benefit = selectedBenefit.value;
        if (!benefit) return false;
        const usage = currentPeriodUsage.value;
        let periodLimit;
        if (isCountBased.value) {
            periodLimit =
                parseFloat(benefit?.benefit?.value_details?.count) || 0;
        } else {
            periodLimit = displayAmount.value;
        }
        return periodLimit > 0 && usage >= periodLimit;
    });

    const currentPeriodUsage = computed(() => {
        const benefit = selectedBenefit.value;
        if (!benefit) return 0;
        const recalculated = store.recalculateCurrentPeriodUsage(benefit);
        return recalculated.current_period_usage || 0;
    });

    const showIneligibleMessage = computed(() => {
        return (
            !parseFloat(displayAmount.value) &&
            selectedBenefit.value?.benefit?.reset_basis === 'usage'
        );
    });

    const expirationTime = computed(() => {
        if (shouldHideExpirationForTsa.value) return null;
        return store.getExpirationTime(
            selectedBenefit.value?.period_end_date,
            selectedBenefit.value?.benefit?.period_type
        );
    });

    const shouldHideExpirationForTsa = computed(() => {
        const category =
            selectedBenefit.value?.benefit?.category?.toLowerCase();
        const periodType =
            selectedBenefit.value?.benefit?.period_type?.toLowerCase();
        const usageRecords = selectedBenefit.value?.usage_records || [];
        return (
            category === 'tsa' &&
            (periodType === 'quinquennial' || periodType === 'quadrennial') &&
            usageRecords.length === 0
        );
    });

    const renewalTooltipText = computed(() => {
        const periodEndDate = selectedBenefit.value?.period_end_date;
        if (!periodEndDate) return 'Renewal date not available';
        const renewalDate = dayjs(periodEndDate).add(1, 'day');
        const resetBasis = selectedBenefit.value?.benefit?.reset_basis;
        if (resetBasis === 'card_anniversary') {
            return `Renews on card anniversary: ${renewalDate.format('MMM D, YYYY')}`;
        }
        return `Renews on ${renewalDate.format('MMM D, YYYY')}`;
    });

    const formattedRenewalLabel = computed(() => {
        const resetBasis = selectedBenefit.value?.benefit?.reset_basis;
        if (resetBasis === 'card_anniversary') return 'Anniversary renewal';
        const periodType =
            store.formatPeriodType(
                selectedBenefit.value?.benefit?.period_type
            ) || 'annually';
        return (
            periodType.charAt(0).toUpperCase() +
            periodType.slice(1).toLowerCase() +
            ' renewal'
        );
    });

    const periodTypeNoun = computed(() => {
        return store.getPeriodTypeFormat(
            selectedBenefit.value?.benefit?.period_type,
            'noun'
        );
    });

    const isAddDisabled = computed(() => {
        return (
            isBenefitFullyRedeemedThisYear.value ||
            selectedBenefit.value?.benefit?.show_first_year_notice ||
            selectedBenefit.value?.is_active === false
        );
    });

    // History allows adding usage even when the benefit is inactive (for logging past usage)
    const isHistoryAddDisabled = computed(() => {
        return (
            isBenefitFullyRedeemedThisYear.value ||
            selectedBenefit.value?.benefit?.show_first_year_notice
        );
    });

    const getCurrentPeriodLabel = () => {
        if (!selectedBenefit.value?.benefit?.period_type)
            return 'Used this period';
        const formattedPeriod = store.formatPeriodType(
            selectedBenefit.value?.benefit?.period_type
        );
        const periodLabels = {
            month: 'Used this month',
            quarter: 'Used this quarter',
            year: 'Used this year',
            '6 months': 'Used this period',
            statement: 'Used this statement period',
        };
        return periodLabels[formattedPeriod] || 'Used this period';
    };

    const formatDate = (dateString) => {
        const localDate = parseLocalDate(dateString);
        return localDate ? dayjs(localDate).format('MMM D, YYYY') : '';
    };

    const validate = () => {
        formErrors.value = {};

        if (
            selectedBenefit.value?.version_history?.length > 0 &&
            !hasActiveVersionForSelectedDate.value
        ) {
            formErrors.value.usageDate =
                'No benefit version was active on this date';
            return false;
        }

        if (!isCountBased.value) {
            if (!usageFormData.value.amount) {
                formErrors.value.usageAmount = 'Enter amount';
            }
            if (!usageFormData.value.date) {
                formErrors.value.usageDate = 'Enter date';
            }
            const effectiveMaxAmount = Math.min(
                parseFloat(remainingAmount.value),
                perTransactionMaxAmount.value
            );
            if (usageFormData.value.amount > effectiveMaxAmount) {
                formErrors.value.usageAmount = `Max amount is ${filters.formatSmartMoney(effectiveMaxAmount)}`;
                if (selectedBenefit.value?.benefit?.value_details?.count > 1) {
                    formErrors.value.usageAmount += ` per use`;
                }
            }
            // By-count benefits are also capped on the number of uses per period
            // (mirrors backend ValidateBenefitUsageCount). Each amount entry is
            // one use, so block once the period's uses are exhausted.
            if (
                isByCountValueBenefit.value &&
                parseFloat(remainingCount.value) < 1
            ) {
                formErrors.value.usageAmount = 'No remaining uses this period';
            }
        } else {
            if (usageFormData.value.count > parseFloat(remainingCount.value)) {
                formErrors.value.usageCount = `Max uses is ${remainingCount.value}`;
            }
            if (!usageFormData.value.count) {
                formErrors.value.usageCount = 'Enter count';
            }
        }

        return Object.keys(formErrors.value).length === 0;
    };

    const openEditUsageModal = (entry) => {
        editingEntry.value = entry;
        usageFormData.value = {
            amount: entry.total_amount_used,
            count: parseInt(entry.usage_count) || 1,
            date: entry.used_at,
            notes: entry.notes || '',
        };
        showEditUsageModal.value = true;
    };

    const openDeleteModal = (entry) => {
        entryToDelete.value = entry;
        deleteFeedback.value = '';
        showDeleteModal.value = true;
    };

    // closeOtherPopover lives in the parent component because it needs to
    // access template refs (currentPeriodRef / redemptionHistoryRef) bound
    // to the child sub-components, which expose their inner popoverRef via
    // defineExpose. The composable cannot own those refs.

    const handleMobileAddUsage = () => {
        setShowAddUsageModal(true);
    };

    const handleUsageSubmit = async (closePopover) => {
        if (!validate()) return;

        const success = await store.handleUsageSubmit(
            usageFormData.value,
            selectedBenefit.value
        );

        if (success) {
            const updatedBenefit = store.benefits.find(
                (benefit) =>
                    benefit?.benefit?.card_benefit_id ===
                        selectedBenefit.value?.benefit?.card_benefit_id &&
                    benefit?.connected_account_id ===
                        selectedBenefit.value?.connected_account_id
            );
            if (updatedBenefit) {
                selectedBenefit.value = updatedBenefit;
            }
            usageFormData.value = {
                amount: '',
                date: dayjs().format('YYYY-MM-DD'),
                notes: '',
            };
            closePopover();
        }
    };

    const confirmDelete = async () => {
        if (!selectedBenefit.value || !entryToDelete.value) return;

        const success = await store.handleUsageDelete(
            selectedBenefit.value,
            entryToDelete.value,
            deleteFeedback.value.trim() || null
        );

        if (success) {
            const updatedBenefit = store.benefits.find(
                (benefit) =>
                    benefit?.benefit?.card_benefit_id ===
                        selectedBenefit.value?.benefit?.card_benefit_id &&
                    benefit?.connected_account_id ===
                        selectedBenefit.value?.connected_account_id
            );
            if (updatedBenefit) {
                selectedBenefit.value = updatedBenefit;
            }
        }

        showDeleteModal.value = false;
        entryToDelete.value = null;
        deleteFeedback.value = '';
    };

    const closeEditUsageModal = () => {
        showEditUsageModal.value = false;
        editingEntry.value = null;
        usageFormData.value = {
            amount: '',
            date: dayjs().format('YYYY-MM-DD'),
            notes: '',
        };
    };

    const handleUsageSaved = async () => {
        if (store.walletId) {
            await store.fetchBenefits(store.walletId);
        }
    };

    const handleUsageUpdated = async (updatedUsage) => {
        const success = await store.handleUsageUpdate(
            updatedUsage,
            selectedBenefit.value,
            editingEntry.value
        );

        if (success) {
            const updatedBenefit = store.benefits.find(
                (benefit) =>
                    benefit?.benefit?.card_benefit_id ===
                        selectedBenefit.value?.benefit?.card_benefit_id &&
                    benefit?.connected_account_id ===
                        selectedBenefit.value?.connected_account_id
            );
            if (updatedBenefit) {
                selectedBenefit.value = updatedBenefit;
            }
            closeEditUsageModal();
        }
    };

    const openEditRenewalDateModal = () => {
        showRenewalDateModal.value = true;
    };

    watch(
        () => usageFormData.value,
        () => {
            formErrors.value = {};
        },
        { deep: true }
    );

    return {
        // Store refs
        store,
        selectedBenefit,
        sidebarOpen,
        showLastFour,
        currentBenefitPosition,
        totalBenefitsCount,
        isFirstBenefit,
        isLastBenefit,

        // Navigation
        closeBenefitDetails,
        navigateToPreviousBenefit,
        navigateToNextBenefit,

        // Mobile
        isMobile,

        // Modal state
        showEditUsageModal,
        showDeleteModal,
        showRenewalDateModal,
        entryToDelete,
        deleteFeedback,
        editingEntry,
        formErrors,
        usageFormData,

        // Computed
        cdnUrl,
        historyGroups,
        labelFor,
        displayAmount,
        isOverrideMonth,
        normalMonthlyValue,
        isCountBased,
        isByCountValueBenefit,
        countLabel,
        remainingAmount,
        remainingCount,
        utilizationPercentage,
        isBenefitFullyRedeemedThisYear,
        isBenefitHidden,
        visibilitySaving,
        showFullyRedeemedIcon,
        currentPeriodUsage,
        showIneligibleMessage,
        expirationTime,
        renewalTooltipText,
        formattedRenewalLabel,
        periodTypeNoun,
        isAddDisabled,
        isHistoryAddDisabled,

        // Methods
        getCurrentPeriodLabel,
        formatDate,
        openEditUsageModal,
        openDeleteModal,
        handleMobileAddUsage,
        handleUsageSubmit,
        confirmDelete,
        closeEditUsageModal,
        handleUsageSaved,
        handleUsageUpdated,
        openEditRenewalDateModal,
        toggleBenefitVisibility,
    };
}
